/**
 * The reading room's model: tools, keys, icons and measurement readouts.
 *
 * Kept apart from the component so the keymap -- which the design's own
 * toolbar map is the authority for -- can be tested without a browser.
 * Two keys the design gives twice are resolved in
 * docs/scope-reading-room.md section 3, and the resolution is here.
 */

/** Icon paths, verbatim from the design (24 x 24, stroked). */
export const ICON = {
  pan: "M12 3v18M3 12h18M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3",
  zoom: "M10.5 4a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM15.5 15.5 20 20M10.5 8v5M8 10.5h5",
  wl: "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M5 19l1.5-1.5M17.5 6.5 19 5",
  stack: "M4 7l8-4 8 4-8 4zM4 12l8 4 8-4M4 17l8 4 8-4",
  length: "M3 17 17 3l4 4L7 21zM7 13l2 2M10 10l2 2M13 7l2 2",
  angle: "M4 20h16M4 20 15 5M9.5 20a5.5 5.5 0 0 0-2.2-4.4",
  cobb: "M3 6l18 3M3 18l18-3M9 7v10",
  probe: "M12 3v5M12 16v5M3 12h5M16 12h5M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z",
  ellipse: "M12 6c5 0 9 2.7 9 6s-4 6-9 6-9-2.7-9-6 4-6 9-6zM12 11v2M11 12h2",
  rect: "M4 6h16v12H4z",
  text: "M4 7V4h16v3M12 4v16M9 20h6",
  ratio: "M19 5 5 19M7.5 5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM16.5 14a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z",
  tags: "M5 4h14v16H5zM8 8h3M13 8h3M8 12h3M13 12h3M8 16h3M13 16h3",
  erase: "M15 4l5 5-10 10H5v-5zM11 8l5 5M9 19h11",
  rotL: "M4 4v5h5M4.6 9A8 8 0 1 1 6 17.7",
  rotR: "M20 4v5h-5M19.4 9A8 8 0 1 0 18 17.7",
  flipH: "M12 3v18M9 7 4 17h5zM15 7l5 10h-5z",
  flipV: "M3 12h18M7 9 17 4v5zM7 15l10 5v-5z",
  invert: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 3v18M12 7h4M12 11h5M12 15h4",
  reset: "M4 12a8 8 0 1 0 2.3-5.6M4 4v4h4",
  mpr: "M12 3l8 4.5v9L12 21l-8-4.5v-9zM12 12l8-4.5M12 12v9M12 12 4 7.5",
  eye: "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
  prevS: "M15 6l-6 6 6 6M6 5v14",
  nextS: "M9 6l6 6-6 6M18 5v14",
  prevI: "M15 6l-6 6 6 6",
  nextI: "M9 6l6 6-6 6",
  play: "M8 5v14l11-7z",
  pause: "M7 5h4v14H7zM13 5h4v14h-4z",
  close: "M6 6l12 12M18 6 6 18",
  expand: "M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5",
  info: "M12 11v5M12 8h.01",
  check: "M5 12l4 4 10-10",
  refLines: "M3 9h18M15 3v18M3 3h18v18H3z",
  film: "M3 5h18v14H3zM3 9h18M3 15h18M10 10.5v3l3-1.5z",
  tile: "M4 4h4v4H4zM10 4h4v4h-4zM16 4h4v4h-4zM4 10h4v4H4zM10 10h4v4h-4zM16 10h4v4h-4zM4 16h4v4H4zM10 16h4v4h-4z",
  sync: "M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1",
} as const;

export type RoomTool =
  | "pan"
  | "zoom"
  | "wl"
  | "stack"
  | "length"
  | "angle"
  | "cobb"
  | "probe"
  | "ellipse"
  | "rect"
  | "ratio"
  | "text"
  | "erase";

export interface RoomToolDef {
  id: RoomTool;
  label: string;
  key: string;
  hint: string;
  /** Cornerstone's own name for it. */
  cs: string;
}

/** The left-button tools, in toolbar order. Labels and hints are the
 *  design's; the probe's key is `U`, not the CT screen's `H` (which the
 *  toolbar map gives to Flip horizontal). */
export const NAVIGATE: readonly RoomToolDef[] = [
  { id: "pan", label: "Pan", key: "P", hint: "Drag to move the image", cs: "Pan" },
  { id: "zoom", label: "Zoom", key: "Z", hint: "Drag up or down to zoom", cs: "Zoom" },
  { id: "wl", label: "Window / level", key: "W", hint: "Drag to adjust window width and level", cs: "WindowLevel" },
  { id: "stack", label: "Scroll", key: "S", hint: "Drag up or down to move through slices", cs: "StackScroll" },
];

export const MEASURE: readonly RoomToolDef[] = [
  { id: "length", label: "Length", key: "L", hint: "Click two points to measure a distance", cs: "Length" },
  { id: "angle", label: "Angle", key: "A", hint: "Click three points to measure an angle", cs: "Angle" },
  { id: "cobb", label: "Cobb angle", key: "C", hint: "Draw two lines to measure the angle between them", cs: "CobbAngle" },
  { id: "probe", label: "HU probe", key: "U", hint: "Click to read the value at a point", cs: "Probe" },
  { id: "ellipse", label: "Ellipse ROI", key: "E", hint: "Drag to get the mean, SD and area", cs: "EllipticalROI" },
  { id: "rect", label: "Rectangle ROI", key: "B", hint: "Drag to draw a rectangular region", cs: "RectangleROI" },
  // Ratio is the Length tool under its own name (`addToolInstance`), so
  // its lines can be paired: the first on an image is A, the next B,
  // and B reads as a percentage of A. `R` is the toolbar map's; the CT
  // screen's reference lines move to Shift+R (scope section 3).
  { id: "ratio", label: "Ratio (%)", key: "R", hint: "Draw two lengths to compare them as a percentage", cs: "Ratio" },
  { id: "text", label: "Text label", key: "T", hint: "Click to place a label or arrow", cs: "ArrowAnnotate" },
  { id: "erase", label: "Erase", key: "X", hint: "Click a measurement to remove it", cs: "Eraser" },
];

export const ALL_TOOLS: readonly RoomToolDef[] = [...NAVIGATE, ...MEASURE];

export function toolById(id: RoomTool): RoomToolDef {
  return ALL_TOOLS.find((t) => t.id === id) ?? NAVIGATE[0]!;
}

// ---------------------------------------------------------------------
// Layouts and views -- docs/scope-reading-room.md section 8.
//
// The LAYOUT says how many cells; the VIEW says what fills them. On the
// stack view a cell holds a SERIES, picked from the rail. On a volume
// view (MPR, MIP, MinIP) the cells are PLANES of the active series, and
// only a series that passes `mprVerdict` can have one.
// ---------------------------------------------------------------------

export type RoomLayout = "1x1" | "2x1" | "2x2";
export type RoomView = "stack" | "mpr" | "mip" | "minip";

/** The slab choices are the design's, in millimetres. */
export const SLABS = [1, 3, 5, 10] as const;
export type Slab = (typeof SLABS)[number];

export function cellCount(layout: RoomLayout): number {
  return layout === "1x1" ? 1 : layout === "2x1" ? 2 : 4;
}

/**
 * The series each stack cell holds after a layout change. Cells already
 * on screen keep what they show; a new cell gets the next series nobody
 * is looking at, and is left EMPTY when there is none -- the reader picks
 * one from the rail -- rather than repeating a series they already have.
 */
export function fillCells(
  previous: readonly (number | null)[],
  layout: RoomLayout,
  seriesCount: number,
): (number | null)[] {
  const count = cellCount(layout);
  const cells = previous.slice(0, count);
  while (cells.length < count) {
    const shown = new Set(cells.filter((c): c is number => c !== null));
    let next: number | null = null;
    for (let i = 0; i < seriesCount; i++) {
      if (!shown.has(i)) {
        next = i;
        break;
      }
    }
    cells.push(next);
  }
  return cells;
}

export interface RoomPlane {
  key: "axial" | "coronal" | "sagittal" | "axial-second";
  label: string;
  /** The plane's colour: its dot, and its line on the other planes. */
  colour: string;
  /** A CT-only second window for the fourth cell, as the design has it. */
  window?: { width: number; centre: number; name: string };
}

const AXIAL: RoomPlane = { key: "axial", label: "Axial", colour: "#3D8BF0" };
const CORONAL: RoomPlane = { key: "coronal", label: "Coronal", colour: "#D07A1F" };
const SAGITTAL: RoomPlane = { key: "sagittal", label: "Sagittal", colour: "#B06BE0" };

/**
 * The planes a volume view draws. The design's 2 x 2 puts the axial at a
 * lung window in the fourth cell; Hounsfield is absolute, so that is a
 * meaningful second look on CT and a number applied to arbitrary units
 * anywhere else -- the same reason the presets are CT only. Off CT the
 * fourth cell is left empty and says why.
 */
export function planesFor(layout: RoomLayout, modality: string | null): (RoomPlane | null)[] {
  if (layout === "1x1") return [AXIAL];
  if (layout === "2x1") return [AXIAL, CORONAL];
  const ct = modality?.toUpperCase() === "CT";
  return [
    AXIAL,
    CORONAL,
    SAGITTAL,
    ct
      ? {
          key: "axial-second",
          label: "Axial · lung",
          colour: "#3D8BF0",
          window: { width: 1500, centre: -600, name: "Lung" },
        }
      : null,
  ];
}

export type Vec3 = readonly [number, number, number];

/**
 * The patient direction a screen direction points along, in LPS: +x is
 * the patient's Left, +y Posterior, +z Superior.
 *
 * A second letter is added when the image is far enough off-axis for it
 * to matter (about 12 degrees): "AL" on an oblique, never three letters
 * of rounding noise on a true axial.
 */
export function letterFor(v: Vec3): string {
  const names: [string, string][] = [
    ["L", "R"],
    ["P", "A"],
    ["S", "I"],
  ];
  const len = Math.hypot(v[0], v[1], v[2]) || 1;
  return [0, 1, 2]
    .map((axis) => ({ axis, c: v[axis]! / len }))
    .filter(({ c }) => Math.abs(c) > 0.2)
    .sort((a, b) => Math.abs(b.c) - Math.abs(a.c))
    .slice(0, 2)
    .map(({ axis, c }) => names[axis]![c > 0 ? 0 : 1])
    .join("");
}

export interface OrientationLetters {
  top: string;
  bottom: string;
  left: string;
  right: string;
}

/** Letters for the four edges, from where screen-right and screen-up
 *  point in the patient. */
export function orientationLetters(right: Vec3, up: Vec3): OrientationLetters {
  const neg = (v: Vec3): Vec3 => [-v[0], -v[1], -v[2]];
  return {
    top: letterFor(up),
    bottom: letterFor(neg(up)),
    left: letterFor(neg(right)),
    right: letterFor(right),
  };
}

/**
 * The colour a SERIES cell wears, from the plane it was acquired in, so
 * that an acquired coronal reads the same as a reformatted one and a
 * reference line drawn from it matches its dot. Grey when oblique: a
 * plane colour on a tilted acquisition would claim a plane it is not.
 */
export function planeColourFor(normal: Vec3 | null): string {
  if (!normal) return "#8D97A5";
  const [x, y, z] = normal.map(Math.abs) as [number, number, number];
  if (z > 0.9) return AXIAL.colour;
  if (y > 0.9) return CORONAL.colour;
  if (x > 0.9) return SAGITTAL.colour;
  return "#8D97A5";
}

/** What a loaded viewport reports about the plane it shows. */
export interface ViewGeometry {
  frameOfReference: string | null;
  normal: Vec3 | null;
}

/** A stack cell's geometry: the viewport's, plus what the case row
 *  stored at upload -- which series it is, and each slice's position. */
export interface CellGeometry extends ViewGeometry {
  series: number;
  /** `slice_position` in slice order: ImagePositionPatient projected on
   *  the slice normal, stored at ingest. Null for a case uploaded before
   *  slice geometry was kept. */
  positions: readonly (number | null)[] | null;
}

export type Link = "active" | "linked" | "other-frame" | "other-plane" | "no-positions" | "empty";

const dot3 = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const complete = (p: CellGeometry["positions"]): p is readonly number[] =>
  !!p && p.length > 0 && p.every((x) => typeof x === "number");

/**
 * Which cells scroll with the active one.
 *
 * BY POSITION, AND ONLY WITHIN ONE FRAME OF REFERENCE AND ONE PLANE.
 * Two series' slice numbers say nothing about each other; their
 * positions do, but only when they share a coordinate system and cut
 * the body the same way. Anything else is left unlinked and says so --
 * a cell that silently refused to follow would read as a broken sync,
 * and one that followed by index would show different anatomy at the
 * "same" place, which is the error the wheel rule exists to prevent.
 *
 * The one exception is the SAME series in two cells, where index and
 * position are the same thing.
 */
export function linksTo(cells: readonly (CellGeometry | null)[], active: number): Link[] {
  const source = cells[active];
  return cells.map((cell, i) => {
    if (i === active) return "active";
    if (!cell) return "empty";
    if (source && cell.series === source.series) return "linked";
    if (!source?.frameOfReference || cell.frameOfReference !== source.frameOfReference) {
      return "other-frame";
    }
    if (!source.normal || !cell.normal || Math.abs(dot3(source.normal, cell.normal)) <= 0.99) {
      return "other-plane";
    }
    return complete(source.positions) && complete(cell.positions) ? "linked" : "no-positions";
  });
}

/**
 * The slice a linked cell should show while the source shows `index`, or
 * null to leave it where it is.
 *
 * Nearest by stored position. Two series acquired in opposite directions
 * have opposite normals, so their projected positions have opposite
 * signs; the normals' dot product corrects it. A position more than one
 * slice gap beyond the target's own range is NOT clamped to its end
 * slice: that slice is different anatomy, and showing it as "the same
 * place" is the failure sync exists to avoid.
 */
export function syncIndex(source: CellGeometry, index: number, target: CellGeometry): number | null {
  if (source.series === target.series) return index;
  const at = source.positions?.[index];
  if (typeof at !== "number" || !complete(target.positions) || !source.normal || !target.normal) return null;
  const want = dot3(source.normal, target.normal) < 0 ? -at : at;
  const positions = target.positions;
  let best = 0;
  for (let i = 1; i < positions.length; i++) {
    if (Math.abs(positions[i]! - want) < Math.abs(positions[best]! - want)) best = i;
  }
  const gaps = positions
    .slice(1)
    .map((p, i) => Math.abs(p - positions[i]!))
    .sort((a, b) => a - b);
  const gap = gaps.length ? gaps[Math.floor(gaps.length / 2)]! : 0;
  return Math.abs(positions[best]! - want) <= Math.max(gap, 0.01) ? best : null;
}

// ---------------------------------------------------------------------
// Phase 4: ultrasound -- docs/scope-reading-room.md section 9.
// ---------------------------------------------------------------------

/**
 * The ultrasound screen's tools, from the design's own strip. Its measure
 * group is in ITS order -- length, ratio, angle, text, Cobb, ellipse,
 * rectangle, erase -- and has no probe: an ultrasound pixel has no unit
 * to read.
 */
export const US_MEASURE: readonly RoomTool[] = ["length", "ratio", "angle", "text", "cobb", "ellipse", "rect", "erase"];
export const US_TOOLS: readonly RoomTool[] = ["pan", "zoom", "wl", ...US_MEASURE];

export function isUltrasound(modality: string | null | undefined): boolean {
  return modality?.toUpperCase() === "US";
}

/** The rows of a case listing the stack is built from. */
export interface StackRow {
  sop_instance_uid: string;
  frame_number?: number | null;
  dicom_url?: string | null;
}

/** One image the viewer can show: a still, or one frame of a clip. */
export interface StackEntry<R extends StackRow = StackRow> {
  imageId: string;
  /** Which instance (still or clip) this is, counting from 0. */
  instance: number;
  /** 1-based frame within a clip; null for a still. */
  frame: number | null;
  /** Frames in this entry's instance: 1 for a still. */
  frames: number;
  /** The instance's first row, for its thumbnail and stored geometry. */
  row: R;
}

/**
 * The stack a series is read as, one entry per picture.
 *
 * A CLIP IS PLAYED FROM ITS DICOM, NOT ITS JPEGS. Ingest keeps at most 60
 * JPEG frames of a clip -- an evenly spaced subsample -- but the
 * constructed file keeps EVERY frame, and its FrameTime. So a clip's
 * frame count is read from the rows (the subsample always includes the
 * last frame, so the highest `frame_number` + 1 is the clip's length) and
 * each frame is addressed in the one stored file as `&frame=n`. Played
 * from the JPEGs, a 90-frame clip would run at two thirds of its
 * frames and skip motion between them.
 *
 * `complete` is false when any instance has no diagnostic file; such a
 * series falls back WHOLE, never per image, for the same reason a
 * part-converted CT does.
 */
export function stackEntries<R extends StackRow>(rows: readonly R[]): { entries: StackEntry<R>[]; complete: boolean } {
  const order: string[] = [];
  const byInstance = new Map<string, R[]>();
  for (const row of rows) {
    const key = row.sop_instance_uid.split("~f")[0]!;
    const bucket = byInstance.get(key);
    if (bucket) bucket.push(row);
    else {
      byInstance.set(key, [row]);
      order.push(key);
    }
  }
  let complete = rows.length > 0;
  const entries: StackEntry<R>[] = [];
  order.forEach((key, instance) => {
    const group = byInstance.get(key)!;
    const url = group.find((r) => r.dicom_url)?.dicom_url;
    if (!url) {
      complete = false;
      return;
    }
    const numbered = group.filter((r) => typeof r.frame_number === "number");
    const frames = numbered.length ? Math.max(...numbered.map((r) => r.frame_number as number)) + 1 : 1;
    const row = group[0]!;
    if (frames === 1) {
      entries.push({ imageId: `wadouri:${url}`, instance, frame: null, frames, row });
      return;
    }
    for (let f = 1; f <= frames; f++) {
      entries.push({ imageId: `wadouri:${url}&frame=${f}`, instance, frame: f, frames, row });
    }
  });
  return { entries, complete };
}

/** How many stills and clips a stack holds, for "Img 2/3". */
export function instanceCount(entries: readonly StackEntry[]): number {
  return entries.length ? entries[entries.length - 1]!.instance + 1 : 0;
}

/**
 * Where cine goes next. ON A CLIP IT LOOPS THE CLIP, which is what an
 * ultrasound cine loop is; it does not run on into the next still. On a
 * still it steps through the series, as the design's cine does.
 */
export function nextCineIndex(entries: readonly StackEntry[], index: number): number {
  const here = entries[index];
  if (!here) return 0;
  if (here.frame !== null) {
    const start = index - (here.frame - 1);
    return here.frame >= here.frames ? start : index + 1;
  }
  return index + 1 >= entries.length ? 0 : index + 1;
}

/**
 * The frame a clip should show `steps` frame-times after it was at
 * `anchor`. Cine keeps to the CLOCK, not to a frame count: a frame that
 * takes longer to decode than the clip allows is skipped, so a 30 Hz clip
 * plays in its real duration. Stepping one frame per tick instead ran it
 * at 21 Hz -- slow motion, which on a moving structure reads as a
 * different finding.
 */
export function cineTarget(entries: readonly StackEntry[], anchor: number, steps: number): number {
  const here = entries[anchor];
  if (!here || here.frame === null) return anchor;
  const start = anchor - (here.frame - 1);
  return start + ((here.frame - 1 + Math.max(0, steps)) % here.frames);
}

/** The cine interval: the clip's own frame time, else the design's 180 ms
 *  for stepping stills (and 120 ms through a CT stack, as before). */
export function cineInterval(entry: StackEntry | undefined, frameTimeMs: number | null, ultrasound: boolean): number {
  if (entry?.frame != null && frameTimeMs && frameTimeMs > 0) return Math.max(10, frameTimeMs);
  return ultrasound ? 180 : 120;
}

/** One image's plane, as its DICOM header gives it. */
export interface ImagePlane {
  position: Vec3;
  rowCosines: Vec3;
  columnCosines: Vec3;
  rows: number;
  columns: number;
  /** Spacing between rows (mm), then between columns. */
  rowSpacing: number;
  columnSpacing: number;
}

const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale = (a: Vec3, k: number): Vec3 => [a[0] * k, a[1] * k, a[2] * k];
const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

export function planeNormal(p: ImagePlane): Vec3 {
  return cross(p.rowCosines, p.columnCosines);
}

/** The image's four corners in patient space, round the edge: TL, TR, BR, BL. */
export function imageCorners(p: ImagePlane): [Vec3, Vec3, Vec3, Vec3] {
  const across = scale(p.rowCosines, (p.columns - 1) * p.columnSpacing);
  const down = scale(p.columnCosines, (p.rows - 1) * p.rowSpacing);
  const tl = p.position;
  return [tl, add(tl, across), add(add(tl, across), down), add(tl, down)];
}

/**
 * Where a rectangle (the source image) crosses a plane (the target
 * image): the two points at which its edges pass through the plane, or
 * null when it does not cross -- parallel, or entirely to one side.
 *
 * This is the reference line, computed from the two images' own headers
 * rather than from either viewport's zoom, so the line spans the whole
 * source slice whatever the reader has panned to.
 */
export function planeCut(
  corners: readonly [Vec3, Vec3, Vec3, Vec3],
  point: Vec3,
  normal: Vec3,
): [Vec3, Vec3] | null {
  const side = corners.map((c) => dot3(sub(c, point), normal));
  const hits: Vec3[] = [];
  for (let i = 0; i < 4; i++) {
    const a = corners[i]!;
    const b = corners[(i + 1) % 4]!;
    const da = side[i]!;
    const db = side[(i + 1) % 4]!;
    if (da === db || (da > 0 && db > 0) || (da < 0 && db < 0)) continue;
    const t = da / (da - db);
    const hit = add(a, scale(sub(b, a), t));
    // An edge ending exactly on the plane is found twice, once per edge.
    if (!hits.some((h) => Math.hypot(...sub(h, hit)) < 1e-6)) hits.push(hit);
  }
  return hits.length >= 2 ? [hits[0]!, hits[1]!] : null;
}

/**
 * Where screen-right and screen-up point in the patient, MEASURED from the
 * viewport's own world-to-canvas mapping rather than read off the camera.
 *
 * The camera's vectors do not say whether a flip was applied to them or
 * to the actor; `worldToCanvas` is what the annotations are drawn with,
 * so it is the one mapping that cannot disagree with the picture. On a
 * viewer an R in the wrong corner is not a cosmetic fault.
 */
export function screenAxes(vp: {
  worldToCanvas: (p: [number, number, number]) => [number, number] | number[];
  getCamera: () => { focalPoint?: readonly number[] };
}): { right: Vec3; up: Vec3 } | null {
  const f = vp.getCamera().focalPoint;
  if (!f || f.length < 3) return null;
  const origin = vp.worldToCanvas([f[0]!, f[1]!, f[2]!]);
  const step = (axis: number) => {
    const p: [number, number, number] = [f[0]!, f[1]!, f[2]!];
    p[axis] = (p[axis] as number) + 10;
    const c = vp.worldToCanvas(p);
    return [c[0]! - origin[0]!, c[1]! - origin[1]!] as const;
  };
  const d = [step(0), step(1), step(2)];
  const right: Vec3 = [d[0]![0], d[1]![0], d[2]![0]];
  const up: Vec3 = [-d[0]![1], -d[1]![1], -d[2]![1]];
  if (!right.some((n) => Math.abs(n) > 1e-6) || !up.some((n) => Math.abs(n) > 1e-6)) return null;
  return { right, up };
}

/** What a single key does, or null. Case-insensitive for letters. */
export type RoomAction =
  | { kind: "tool"; tool: RoomTool }
  | { kind: "rotate"; degrees: 90 | -90 }
  | { kind: "flip"; axis: "h" | "v" }
  | { kind: "invert" }
  | { kind: "reset" }
  | { kind: "mpr" }
  | { kind: "overlays" }
  | { kind: "tags" }
  | { kind: "layout"; layout: RoomLayout }
  | { kind: "refLines" }
  | { kind: "sync" }
  | { kind: "projection"; mode: "mip" | "minip" }
  | { kind: "volumeRender" }
  | { kind: "image"; delta: number }
  | { kind: "series"; delta: 1 | -1 }
  | { kind: "first" }
  | { kind: "last" }
  | { kind: "cine" }
  | { kind: "escape" };

/**
 * `shift` is the modifier, not the case of `key`: with Caps Lock on, a
 * bare R arrives as "R" and must still be Ratio, not reference lines.
 */
export function actionForKey(key: string, shift = false): RoomAction | null {
  if (shift) {
    switch (key.toLowerCase()) {
      case "r":
        return { kind: "refLines" };
      case "m":
        return { kind: "projection", mode: "mip" };
      case "n":
        return { kind: "projection", mode: "minip" };
      case "v":
        return { kind: "volumeRender" };
    }
  }
  const tool = ALL_TOOLS.find((t) => t.key.toLowerCase() === key.toLowerCase());
  if (tool && key.length === 1) return { kind: "tool", tool: tool.id };
  switch (key) {
    case "1":
      return { kind: "layout", layout: "1x1" };
    case "2":
      return { kind: "layout", layout: "2x1" };
    case "4":
      return { kind: "layout", layout: "2x2" };
    case "y":
    case "Y":
      return { kind: "sync" };
    case "[":
      return { kind: "rotate", degrees: -90 };
    case "]":
      return { kind: "rotate", degrees: 90 };
    case "h":
    case "H":
      return { kind: "flip", axis: "h" };
    case "v":
    case "V":
      return { kind: "flip", axis: "v" };
    case "i":
    case "I":
      return { kind: "invert" };
    case "0":
      return { kind: "reset" };
    case "m":
    case "M":
      return { kind: "mpr" };
    case "o":
    case "O":
      return { kind: "overlays" };
    case "d":
    case "D":
      return { kind: "tags" };
    case "ArrowUp":
    case "ArrowLeft":
      return { kind: "image", delta: -1 };
    case "ArrowDown":
    case "ArrowRight":
      return { kind: "image", delta: 1 };
    case "PageUp":
      return { kind: "series", delta: -1 };
    case "PageDown":
      return { kind: "series", delta: 1 };
    case "Home":
      return { kind: "first" };
    case "End":
      return { kind: "last" };
    case " ":
      return { kind: "cine" };
    case "Escape":
      return { kind: "escape" };
    default:
      return null;
  }
}

/** One drawn annotation, as the panel lists it. */
export interface Measurement {
  uid: string;
  tool: string;
  label: string;
  value: string;
  detail: string | null;
  imageId: string;
  /** A ratio line's length, kept numeric so its pair can be computed. */
  amount?: number;
}

const LABELS: Record<string, string> = {
  Length: "Length",
  Angle: "Angle",
  CobbAngle: "Cobb angle",
  Probe: "Probe",
  EllipticalROI: "Ellipse ROI",
  RectangleROI: "Rectangle ROI",
  Ratio: "Ratio",
  ArrowAnnotate: "Text label",
};

function fixed(n: unknown, digits: number): string | null {
  return typeof n === "number" && Number.isFinite(n) ? n.toFixed(digits) : null;
}

/**
 * Read Cornerstone's cached statistics into one line for the panel.
 *
 * The stats are keyed by a target id nobody else needs; the first entry
 * is the image the annotation was drawn on. Units come from the stats
 * themselves -- "mm" with pixel spacing, "px" without, "HU" on CT -- so
 * the panel never claims a millimetre the file could not support.
 */
export function readMeasurement(annotation: {
  annotationUID?: string;
  metadata?: { toolName?: string; referencedImageId?: string };
  data?: { cachedStats?: Record<string, Record<string, unknown>>; text?: string };
}): Measurement | null {
  const tool = annotation.metadata?.toolName ?? "";
  const label = LABELS[tool];
  const uid = annotation.annotationUID;
  const imageId = annotation.metadata?.referencedImageId;
  if (!label || !uid || !imageId) return null;
  // The entry that holds numbers, not merely the first: a tool seeds its
  // stats with nulls and fills them on the next render.
  const entries = Object.values(annotation.data?.cachedStats ?? {});
  const stats =
    entries.find((e) =>
      Object.values(e).some((v) => typeof v === "number" && Number.isFinite(v)),
    ) ?? entries[0] ?? {};

  let value: string | null = null;
  let detail: string | null = null;
  let amount: number | undefined;
  switch (tool) {
    case "ArrowAnnotate":
      value = annotation.data?.text?.trim() || null;
      break;
    case "Ratio": {
      const v = fixed(stats.length, 1);
      value = v ? `${v} ${String(stats.unit ?? "mm")}` : null;
      if (typeof stats.length === "number" && Number.isFinite(stats.length)) amount = stats.length;
      break;
    }
    case "Length": {
      const v = fixed(stats.length, 1);
      value = v ? `${v} ${String(stats.unit ?? "mm")}` : null;
      break;
    }
    case "Angle":
    case "CobbAngle": {
      const v = fixed(stats.angle, 1);
      value = v ? `${v}°` : null;
      break;
    }
    case "Probe": {
      const v = fixed(stats.value, 0);
      value = v ? `${v} ${String(stats.modalityUnit ?? "")}`.trim() : null;
      break;
    }
    case "EllipticalROI":
    case "RectangleROI": {
      const mean = fixed(stats.mean, 0);
      value = mean ? `${mean} ${String(stats.modalityUnit ?? "")}`.trim() : null;
      const sd = fixed(stats.stdDev, 0);
      const area = fixed(stats.area, 1);
      const parts = [
        sd ? `SD ${sd}` : null,
        area ? `${area} ${String(stats.areaUnit ?? "")}`.trim() : null,
      ].filter(Boolean);
      detail = parts.length ? parts.join(" · ") : null;
      break;
    }
  }
  return {
    uid,
    tool,
    label,
    value: value ?? "—",
    detail,
    imageId,
    ...(amount !== undefined ? { amount } : {}),
  };
}

/**
 * The text a ratio line draws on the image, from its place among the
 * ratio lines on the same image (creation order) and their lengths.
 *
 * Lines pair up in order -- 1st A, 2nd B, 3rd A of the next pair -- and
 * B carries the percentage. A B whose A has no length yet says only its
 * own length: a percentage of nothing is not a number worth drawing.
 */
export function ratioText(
  lengths: readonly (number | null)[],
  index: number,
  unit: string,
): string[] {
  const own = lengths[index];
  if (typeof own !== "number" || !Number.isFinite(own)) return [];
  const len = own.toFixed(1);
  if (index % 2 === 0) return [`A ${len} ${unit}`];
  const a = lengths[index - 1];
  const pct = typeof a === "number" && a > 0 ? Math.round((own / a) * 100) : null;
  return pct === null ? [`B ${len} ${unit}`] : [`B ${len} ${unit}`, `${pct}% of A`];
}

/** Label ratio lines A / B in the panel, the same way the image does. */
export function pairRatios(list: readonly Measurement[]): Measurement[] {
  const seen = new Map<string, Measurement[]>();
  return list.map((m) => {
    if (m.tool !== "Ratio") return m;
    const onImage = seen.get(m.imageId) ?? [];
    seen.set(m.imageId, [...onImage, m]);
    const index = onImage.length;
    if (index % 2 === 0) return { ...m, label: "Ratio A" };
    const a = onImage[index - 1]?.amount;
    const pct = a && a > 0 && m.amount !== undefined ? Math.round((m.amount / a) * 100) : null;
    return { ...m, label: "Ratio B", detail: pct === null ? m.detail : `${pct}% of A` };
  });
}

/**
 * Names for every tag the stored file can carry.
 *
 * Generated 2026-10-07 from `backend/app/assessments/dicom_deident.py`
 * (`ALLOWED` plus the regenerated UIDs) through pydicom's dictionary.
 * The stored file is CONSTRUCTED from that list, so these are all the
 * tags there are. A tag missing from this table means the allowlist grew
 * without this following it; the tags view shows such a tag by number
 * rather than hiding it.
 */
export const STORED_TAGS: Readonly<Record<string, readonly [string, string]>> = {
  x00080016: ["SOPClassUID", "SOP Class UID"],
  x00080018: ["SOPInstanceUID", "SOP Instance UID"],
  x00080060: ["Modality", "Modality"],
  x0008103e: ["SeriesDescription", "Series Description"],
  x00180015: ["BodyPartExamined", "Body Part Examined"],
  x00180040: ["CineRate", "Cine Rate"],
  x00180050: ["SliceThickness", "Slice Thickness"],
  x00180088: ["SpacingBetweenSlices", "Spacing Between Slices"],
  x00181063: ["FrameTime", "Frame Time"],
  x00181164: ["ImagerPixelSpacing", "Imager Pixel Spacing"],
  x00186011: ["SequenceOfUltrasoundRegions", "Sequence of Ultrasound Regions"],
  x0020000d: ["StudyInstanceUID", "Study Instance UID"],
  x0020000e: ["SeriesInstanceUID", "Series Instance UID"],
  x00200011: ["SeriesNumber", "Series Number"],
  x00200013: ["InstanceNumber", "Instance Number"],
  x00200032: ["ImagePositionPatient", "Image Position (Patient)"],
  x00200037: ["ImageOrientationPatient", "Image Orientation (Patient)"],
  x00200052: ["FrameOfReferenceUID", "Frame of Reference UID"],
  x00280002: ["SamplesPerPixel", "Samples per Pixel"],
  x00280004: ["PhotometricInterpretation", "Photometric Interpretation"],
  x00280006: ["PlanarConfiguration", "Planar Configuration"],
  x00280008: ["NumberOfFrames", "Number of Frames"],
  x00280010: ["Rows", "Rows"],
  x00280011: ["Columns", "Columns"],
  x00280030: ["PixelSpacing", "Pixel Spacing"],
  x00280100: ["BitsAllocated", "Bits Allocated"],
  x00280101: ["BitsStored", "Bits Stored"],
  x00280102: ["HighBit", "High Bit"],
  x00280103: ["PixelRepresentation", "Pixel Representation"],
  x00280301: ["BurnedInAnnotation", "Burned In Annotation"],
  x00281050: ["WindowCenter", "Window Center"],
  x00281051: ["WindowWidth", "Window Width"],
  x00281052: ["RescaleIntercept", "Rescale Intercept"],
  x00281053: ["RescaleSlope", "Rescale Slope"],
  x00281054: ["RescaleType", "Rescale Type"],
  x00281056: ["VOILUTFunction", "VOI LUT Function"],
};

/**
 * Tags the file carries that are not on the allowlist because the
 * allowlist is about the DATASET: the file-meta group pydicom writes, and
 * the pixels themselves. Named so the tags view does not present them as
 * something that slipped through.
 */
const FILE_TAGS: Readonly<Record<string, readonly [string, string]>> = {
  x00020000: ["FileMetaInformationGroupLength", "File Meta Information Group Length"],
  x00020001: ["FileMetaInformationVersion", "File Meta Information Version"],
  x00020002: ["MediaStorageSOPClassUID", "Media Storage SOP Class UID"],
  x00020003: ["MediaStorageSOPInstanceUID", "Media Storage SOP Instance UID"],
  x00020010: ["TransferSyntaxUID", "Transfer Syntax UID"],
  x00020012: ["ImplementationClassUID", "Implementation Class UID"],
  x00020013: ["ImplementationVersionName", "Implementation Version Name"],
  x7fe00010: ["PixelData", "Pixel Data"],
};

/** The parts of a dicom-parser DataSet the tags view reads. */
export interface TagSource {
  elements: Record<string, { vr?: string; length: number; items?: unknown[] }>;
  string(tag: string): string | undefined;
  uint16(tag: string, index?: number): number | undefined;
  int16(tag: string, index?: number): number | undefined;
  uint32(tag: string, index?: number): number | undefined;
  int32(tag: string, index?: number): number | undefined;
  float(tag: string, index?: number): number | undefined;
  double(tag: string, index?: number): number | undefined;
}

export interface TagRow {
  tag: string;
  keyword: string | null;
  name: string;
  vr: string;
  value: string;
}

const NUMERIC: Record<string, [keyof TagSource, number]> = {
  US: ["uint16", 2],
  SS: ["int16", 2],
  UL: ["uint32", 4],
  SL: ["int32", 4],
  FL: ["float", 4],
  FD: ["double", 8],
};

/**
 * Every element in one stored file, in tag order, with a readable value.
 *
 * What it shows is what the FILE holds, not what the allowlist says it
 * should: a tag with no name here is listed by number rather than hidden,
 * because the tags view is the one place somebody can check the
 * construction with their own eyes.
 */
export function describeTags(ds: TagSource): TagRow[] {
  return Object.keys(ds.elements)
    .sort()
    .map((key) => {
      const el = ds.elements[key]!;
      const vr = el.vr ?? "";
      const known = STORED_TAGS[key] ?? FILE_TAGS[key];
      const tag = `(${key.slice(1, 5)},${key.slice(5, 9)})`.toUpperCase();
      let value: string;
      const numeric = NUMERIC[vr];
      if (key === "x7fe00010" || vr === "OB" || vr === "OW" || vr === "UN") {
        value = `${el.length.toLocaleString("en-AU")} bytes`;
      } else if (vr === "SQ") {
        const n = el.items?.length ?? 0;
        value = `${n} item${n === 1 ? "" : "s"}`;
      } else if (numeric) {
        const [read, size] = numeric;
        const count = Math.max(1, Math.floor(el.length / size));
        const values: string[] = [];
        for (let i = 0; i < Math.min(count, 16); i++) {
          const v = (ds[read] as (tag: string, index?: number) => number | undefined).call(ds, key, i);
          if (v !== undefined) values.push(String(v));
        }
        value = values.join(" \\ ") + (count > 16 ? " …" : "");
      } else {
        value = (ds.string(key) ?? "").split("\\").join(" \\ ");
      }
      return {
        tag,
        keyword: known?.[0] ?? null,
        name: known?.[1] ?? "Unnamed — not in the viewer's tag list",
        vr,
        value,
      };
    });
}
